// src/theme.js
// One place for the app's look and feel: brand palette, rounded soft-shadow
// surfaces, readable tables and gradient primary actions. Built per mode so
// light and dark share the same shapes and only swap colours.
import { createTheme, alpha } from '@mui/material/styles';

const BRAND = {
    main: '#1b3f94',
    light: '#3f68c9',
    dark: '#112a68',
    accent: '#e8112d',
};

export const buildTheme = (darkMode) => {
    const mode = darkMode ? 'dark' : 'light';
    const border = darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(27,63,148,0.10)';
    const surfaceTint = darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(27,63,148,0.05)';
    const softShadow = darkMode
        ? '0 2px 12px rgba(0,0,0,0.35)'
        : '0 2px 14px rgba(27,63,148,0.08)';

    return createTheme({
        palette: {
            mode,
            primary: {
                main: darkMode ? '#6f93ec' : BRAND.main,
                light: BRAND.light,
                dark: BRAND.dark,
                contrastText: '#ffffff',
            },
            secondary: { main: BRAND.accent },
            background: {
                default: darkMode ? '#0e1523' : '#eef2f9',
                paper: darkMode ? '#172033' : '#ffffff',
            },
            divider: border,
            text: darkMode
                ? { primary: '#e8edf7', secondary: '#9aa7c0' }
                : { primary: '#1c2540', secondary: '#5b6785' },
        },
        shape: { borderRadius: 12 },
        typography: {
            fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
            h4: { fontWeight: 700, letterSpacing: '-0.01em' },
            h5: { fontWeight: 700, letterSpacing: '-0.01em' },
            h6: { fontWeight: 600 },
            subtitle1: { fontWeight: 600 },
            button: { textTransform: 'none', fontWeight: 600, letterSpacing: 0 },
        },
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    body: { backgroundImage: 'none' },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: { borderRadius: 14, backgroundImage: 'none' },
                    elevation1: { boxShadow: softShadow, border: `1px solid ${border}` },
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        borderRadius: 14,
                        border: `1px solid ${border}`,
                        boxShadow: softShadow,
                        transition: 'transform .2s ease, box-shadow .2s ease',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: darkMode
                                ? '0 8px 24px rgba(0,0,0,0.45)'
                                : '0 10px 28px rgba(27,63,148,0.16)',
                        },
                    },
                },
            },
            MuiAppBar: {
                defaultProps: { elevation: 0 },
                styleOverrides: {
                    root: {
                        backgroundImage: `linear-gradient(90deg, ${BRAND.dark} 0%, ${BRAND.main} 55%, ${BRAND.light} 100%)`,
                        borderBottomLeftRadius: 0,
                        boxShadow: '0 4px 20px rgba(17,42,104,0.25)',
                    },
                },
            },
            MuiButton: {
                defaultProps: { disableElevation: true },
                styleOverrides: {
                    root: { borderRadius: 10, paddingInline: 16 },
                    containedPrimary: {
                        backgroundImage: `linear-gradient(135deg, ${BRAND.main}, ${BRAND.light})`,
                        boxShadow: '0 4px 12px rgba(27,63,148,0.28)',
                        '&:hover': {
                            backgroundImage: `linear-gradient(135deg, ${BRAND.dark}, ${BRAND.main})`,
                            boxShadow: '0 6px 16px rgba(27,63,148,0.38)',
                        },
                    },
                    outlinedPrimary: {
                        borderColor: alpha(BRAND.main, darkMode ? 0.5 : 0.35),
                        '&:hover': { backgroundColor: surfaceTint },
                    },
                },
            },
            MuiIconButton: {
                styleOverrides: { root: { borderRadius: 10 } },
            },
            MuiChip: {
                styleOverrides: {
                    root: { fontWeight: 600, borderRadius: 8 },
                },
            },
            MuiTextField: {
                defaultProps: { variant: 'outlined' },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: 10,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: alpha(BRAND.light, 0.8),
                        },
                    },
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: { borderBottomColor: border },
                    head: {
                        fontWeight: 700,
                        fontSize: '0.78rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        color: darkMode ? '#b9c6e4' : BRAND.dark,
                        backgroundColor: darkMode ? '#1d2840' : '#e9eefb',
                    },
                },
            },
            MuiTableRow: {
                styleOverrides: {
                    root: {
                        '&.MuiTableRow-hover:hover': { backgroundColor: surfaceTint },
                    },
                },
            },
            MuiTablePagination: {
                styleOverrides: {
                    root: { borderTop: `1px solid ${border}` },
                },
            },
            MuiDialog: {
                styleOverrides: {
                    paper: {
                        borderRadius: 18,
                        boxShadow: '0 24px 60px rgba(10,20,50,0.35)',
                    },
                },
            },
            MuiDialogTitle: {
                styleOverrides: { root: { fontWeight: 700 } },
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: { borderRadius: 8, fontSize: '0.75rem', fontWeight: 500 },
                },
            },
            MuiAlert: {
                styleOverrides: { root: { borderRadius: 12 } },
            },
            MuiListItemButton: {
                styleOverrides: { root: { borderRadius: 10 } },
            },
        },
    });
};

export { BRAND };
